/*
  Spring Water driver role: ARCH_SWRA_DRIVER_<plant_code> (e.g. ARCH_SWRA_DRIVER_HOU).

  Like the plant manager role (ARCH_PM_<plant_code>) it is scoped to plants. A user with only
  driver roles sees nothing but Spring Water (Pumps and Queue, check-in only) at those plants.
  Combined with other roles the user keeps what those roles give, plus Spring Water at the
  driver plants. Page and action rules: pages/master/UsecaseDashboard/SpringWater/utils/access.ts

  The plant manager role gives no Spring Water access: Spring Water is hidden from users without
  one of the roles below (ARCH_API swra accessFromRoles canView).
*/

export const SWRA_DRIVER_ROLE_PREFIX = 'ARCH_SWRA_DRIVER_'
const SWRA_USECASE_ROLE = 'ARCH_USECASE_SWRA'

// Roles that open Spring Water, besides the driver role
const SWRA_VIEW_ROLES = [
	'ARCH_ADMIN',
	'ARCH_DEVELOPER',
	'ARCH_STAKEHOLDER',
	'ARCH_SCM_ADMIN',
	SWRA_USECASE_ROLE,
	'ARCH_USECASE_ALL'
]

export const hasSwraDriverRole = (permissions: string[] = []) =>
	permissions.some(role => role.startsWith(SWRA_DRIVER_ROLE_PREFIX))

/** The user may open Spring Water (plant managers and asset managers may not) */
export const canOpenSpringWater = (permissions: string[] = []) =>
	permissions.some(role => SWRA_VIEW_ROLES.includes(role)) ||
	hasSwraDriverRole(permissions)

/** Spring Water opens only through driver roles: Pumps & Queue only (no Alerts, History, Reports) */
export const isSwraDriverUser = (permissions: string[] = []) =>
	hasSwraDriverRole(permissions) &&
	!permissions.some(role => SWRA_VIEW_ROLES.includes(role))

/** Plant codes of the driver roles (ARCH_SWRA_DRIVER_HOU -> HOU) */
export const swraDriverPlantCodes = (permissions: string[] = []) =>
	permissions
		.filter(role => role.startsWith(SWRA_DRIVER_ROLE_PREFIX))
		.map(role => role.slice(SWRA_DRIVER_ROLE_PREFIX.length))

/** The user has driver roles and no other ARCH role */
export const isSwraDriverOnly = (permissions: string[] = []) =>
	permissions.length > 0 &&
	permissions.every(role => role.startsWith(SWRA_DRIVER_ROLE_PREFIX))

/**
 * The use case champion roles a driver role stands for when filtering use cases and menus:
 * Spring Water. Nothing when a plant manager or asset manager role already shows every use case
 * of the user's plants, so a combination keeps the other role's use cases.
 */
export const swraDriverUsecaseRoles = (permissions: string[] = []): string[] =>
	hasSwraDriverRole(permissions) &&
	!permissions.some(
		role =>
			role.startsWith('ARCH_PM_') ||
			role === 'ARCH_ASSET_MANAGER' ||
			role === SWRA_USECASE_ROLE
	)
		? [SWRA_USECASE_ROLE]
		: []

/**
 * Spring Water is the user's only access: driver roles and / or the Spring Water use case
 * champion, no other ARCH role. These users get the "Touch to begin" screen before Pumps & Queue
 * (pages/master/UsecaseDashboard/SpringWater/components/TouchToBegin.tsx).
 */
export const isSwraOnlyUser = (permissions: string[] = []) =>
	permissions.length > 0 &&
	permissions.every(
		role => role.startsWith(SWRA_DRIVER_ROLE_PREFIX) || role === SWRA_USECASE_ROLE
	)
